import * as React from 'react';
import { compose, setDisplayName, withProps } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { State } from 'reducers';
import { CellIndex, MinutesPerSegment, getNextZoomLevel } from 'types';
import { Link, Unlink, ZoomIn, ZoomOut } from 'ui/shapes';
import { getLayout, getMinutesPerSegment, getSync } from 'selectors/monitoringSelectors';
import { setZoom, setSync } from 'actions/monitoringActions';

import './ZoomControls.css';

type Props = MergeProps & RecomposeProps & TranslateProps;
const RawZoomControls = ({ cellIndex, showSync, sync, toggleSync, canZoomIn, canZoomOut, zoomIn, zoomOut, styles, t }: Props) =>
    <div className="buttons zoom-controls">
        <button id={ `zoom-in-button-${cellIndex}` } title={ t('1558') } disabled={ !canZoomIn } onClick={ zoomIn }>
            <ZoomIn
                width="1em"
                height="1em"
                style={ styles.foreground }
            />
        </button>

        <button id={ `zoom-out-button-${cellIndex}` } title={ t('1559') } disabled={ !canZoomOut } onClick={ zoomOut }>
            <ZoomOut
                width="1em"
                height="1em"
                style={ styles.foreground }
            />
        </button>
        { showSync &&
            <button id={ `sync-zoom-button-${cellIndex}` } title={ t(sync ? 'WC_SYNC_TIMELINE' : 'WC_FREE_TIMELINE') } onClick={ toggleSync }>
                { sync
                    ? <Link width="1em" height="1em" style={ styles.foreground }/>
                    : <Unlink width="1em" height="1em" style={ styles.foreground }/>
                }
            </button>
        }
    </div>
;

export type OwnProps = {
    cellIndex: CellIndex;
};
type RecomposeProps = {
    styles: { [name: string]: React.CSSProperties };
};
type StateProps = {
    showSync: boolean;
    sync: boolean;
    canZoomIn: boolean;
    canZoomOut: boolean;
    zoom: MinutesPerSegment;
};
type DispProps = {
    setZoom: (z: MinutesPerSegment) => () => void;
    setSync: (sz: boolean) => () => void;
};
type MergeProps = OwnProps & {
    showSync: boolean;
    sync: boolean;
    canZoomIn: boolean;
    canZoomOut: boolean;
    zoomIn: () => void;
    zoomOut: () => void;
    toggleSync: () => void;
};

const mapStateToProps = (state: State, ownProps: OwnProps): StateProps => {
    const zoom = getMinutesPerSegment(state, ownProps);
    return {
        showSync: getLayout(state) > 1,
        sync: getSync(state, ownProps),
        canZoomIn: zoom !== getNextZoomLevel(zoom, true),
        canZoomOut: zoom !== getNextZoomLevel(zoom, false),
        zoom,
    };
};

const mapDispatchToProps = (dispatch: Function, { cellIndex }: OwnProps): DispProps => ({
    setZoom: minutesPerSegment => () => dispatch(setZoom({ cellIndex, minutesPerSegment })),
    setSync: sync => () => dispatch(setSync({ cellIndex, sync })),
});

const mergeProps = ({ zoom, ...stateProps }: StateProps, { setZoom, setSync }: DispProps, ownProps: OwnProps): MergeProps => ({
    ...stateProps,
    zoomIn: setZoom(getNextZoomLevel(zoom, true)),
    zoomOut: setZoom(getNextZoomLevel(zoom, false)),
    toggleSync: setSync(!stateProps.sync),
    ...ownProps,
});

export const ZoomControls = compose<Props, OwnProps>(
    setDisplayName('ZoomControls'),
    skin,
    translate(),
    withProps(({ styles }: OemProps) => ({
        styles: {
            ...styles,
            foreground: {
                fill: styles.Window.color,
            },
        },
    })),
    connect(mapStateToProps, mapDispatchToProps, mergeProps),
)(RawZoomControls);



// WEBPACK FOOTER //
// ./src/ui/pages/monitoring/mainPanel/timeline/ZoomControls.tsx